import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { notificationAPI } from '../services/api';
import { formatInline } from '../utils/messageFormatter';

const NotificationContext = createContext(null);

export function useNotifications() {
  return useContext(NotificationContext);
}

function buildWSUrl() {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/api/ws`;
}

// ── Toast renderer for incoming WS notifications ──────────────────────────────

function NotificationToast({ notification }) {
  if (!notification) return null;
  const cfg = SEVERITY_CONFIG[notification.severity] || SEVERITY_INFO;
  const p = notification.payload || {};

  return (
    <div
      className="rounded-lg px-3 py-2.5 min-w-[300px] max-w-[420px] bg-surface-1 text-ink text-xs shadow-lg"
      style={{ border: `1px solid ${cfg.color}`, borderLeft: `4px solid ${cfg.color}` }}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.color }} />
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: cfg.color }}>{cfg.label}</span>
        <span className="text-[10px] text-ink-dim ml-auto">{notification.brand?.toUpperCase()}</span>
      </div>
      {p.title && <p className="text-xs font-bold text-ink mb-0.5">{p.title}</p>}
      {p.message && <p className="text-xs leading-relaxed text-ink-mute">{formatInline(getFirstLine(p.message))}</p>}
    </div>
  );
}

function NotificationToastItem({ item, onDismiss }) {
  const duration = item.notification.severity === 'critical' ? 15000 : 7000;

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(item.id), duration);
    return () => clearTimeout(timer);
  }, [item.id, onDismiss, duration]);

  return (
    <button onClick={() => onDismiss(item.id)} className="block text-left animate-slideIn">
      <NotificationToast notification={item.notification} />
    </button>
  );
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [toastQueue, setToastQueue] = useState([]); // [{ notification, id }]
  const [lastNotification, setLastNotification] = useState(null);
  const [vipDataUpdate, setVipDataUpdate] = useState(null); // { brand, at }; brand null = every brand
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const mountedRef = useRef(true);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await notificationAPI.getUnreadCount();
      if (mountedRef.current) setUnreadCount(res.data?.count ?? 0);
    } catch {}
  }, [user]);

  const connect = useCallback(() => {
    if (!user || !mountedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(buildWSUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      fetchUnreadCount();
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.event === 'notification' && msg.data) {
          setUnreadCount(prev => prev + 1);
          setToastQueue(prev => [...prev, { notification: msg.data, id: Date.now() + Math.random() }]);
          setLastNotification(msg.data);
        } else if (msg.event === 'vip_data_updated') {
          setVipDataUpdate({ brand: msg.brand || null, at: Date.now() });
        }
      } catch {}
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      // Reconnect after 3s
      reconnectTimer.current = setTimeout(connect, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [user, fetchUnreadCount]);

  useEffect(() => {
    mountedRef.current = true;
    if (user) {
      fetchUnreadCount();
      connect();
    }
    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [user, connect, fetchUnreadCount]);

  const dismissToast = useCallback((id) => {
    setToastQueue(prev => prev.filter(t => t.id !== id));
  }, []);

  const decrementUnread = useCallback((by = 1) => {
    setUnreadCount(prev => Math.max(0, prev - by));
  }, []);

  const resetUnread = useCallback(() => {
    setUnreadCount(0);
  }, []);

  return (
    <NotificationContext.Provider value={{ unreadCount, fetchUnreadCount, decrementUnread, resetUnread, lastNotification, vipDataUpdate }}>
      {children}

      {toastQueue.length > 0 && (
        <div className="fixed top-4 right-4 z-[9999] flex flex-col items-end gap-2">
          {toastQueue.map(item => (
            <NotificationToastItem key={item.id} item={item} onDismiss={dismissToast} />
          ))}
        </div>
      )}
    </NotificationContext.Provider>
  );
}

// ── Severity config (colour + label) ─────────────────────────────────────────

export const SEVERITY_INFO = { color: '#3861FB', label: 'Info' };
export const SEVERITY_SUCCESS = { color: '#00BD71', label: 'Success' };
export const SEVERITY_WARNING = { color: '#FFD22F', label: 'Warning' };
export const SEVERITY_CRITICAL = { color: '#E21351', label: 'Critical' };

export const SEVERITY_CONFIG = {
  info: SEVERITY_INFO,
  success: SEVERITY_SUCCESS,
  warning: SEVERITY_WARNING,
  critical: SEVERITY_CRITICAL,
};

export const SEVERITY_ORDER = ['critical', 'warning', 'success', 'info'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getFirstLine(message) {
  if (!message) return '';
  return message.split('\n')[0];
}
